import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import type {
  ICertificate,
  ICertificateEdit,
  ICertificateForm,
  ICertificateGenerate,
  ICertificateUploadForm,
  IUSuariosVerificadosCertificado,
} from 'models/certificate_model';
import {
  getCertificatesService,
  getPlantillasService,
  verificarUsuariosCertificadoService,
  createCertificateService,
  editCertificateService,
  deleteCertificateService,
  uploadCertificateService,
  downloadCertificateService,
  generarCertificadoTemporalService,
  generarCertificadoTemporalExistenteService,
  descargarCSVEjemploPeticion,
} from 'repositories/certificate_repository';
import type { TipoCertificadoGenerado } from 'models/certificate_model';

interface PlantillaItem {
  id: string;
  nombre: string;
  cantidadCaracteres: number;
  fraseInicial: string;
  fraseInicialDe: string | null;
  cantidadCaracteresTematicas: number | null;
}

interface CertificateState {
  userVerification: IUSuariosVerificadosCertificado[];
  plantillas: PlantillaItem[];
  certificates: ICertificate[];
  countCertificates: number;
  loading: boolean;
  error: string | null;
}

const initialState: CertificateState = {
  userVerification: [],
  plantillas: [],
  certificates: [],
  countCertificates: 0,
  loading: false,
  error: null,
};

export const cargarPlantillasCertificados = createAsyncThunk(
  'certificate/cargarPlantillas',
  async (tipo: 'PROFESOR' | 'ESTUDIANTE', { rejectWithValue }) => {
    try {
      const { rows } = await getPlantillasService({ tipo });
      return rows ?? [];
    } catch {
      return rejectWithValue('Error al cargar plantillas');
    }
  },
);

export const cargarCertificados = createAsyncThunk(
  'certificate/cargarCertificados',
  async (
    params: {
      tipoGeneracion: TipoCertificadoGenerado;
      _limit?: number;
      _page?: number;
      nombreUsuario?: string;
      gestion?: string;
      tipoCertificado?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const { rows, count } = await getCertificatesService(params);
      return { rows: rows ?? [], count: count ?? 0 };
    } catch {
      return rejectWithValue('Error al cargar certificados');
    }
  },
);

export const verificarCertificadoUsuarios = createAsyncThunk(
  'certificate/verificarUsuarios',
  async (data: ICertificateForm, { rejectWithValue }) => {
    try {
      const { datos } = await verificarUsuariosCertificadoService(data);
      return datos ?? [];
    } catch {
      return rejectWithValue('Error al verificar usuarios');
    }
  },
);

export const addCertificates = createAsyncThunk(
  'certificate/addCertificates',
  async (data: ICertificateForm, { rejectWithValue }) => {
    try {
      return await createCertificateService(data);
    } catch {
      return rejectWithValue('Error al crear certificado');
    }
  },
);

export const editCertificate = createAsyncThunk(
  'certificate/editCertificate',
  async (data: ICertificateEdit, { rejectWithValue }) => {
    try {
      return await editCertificateService(data);
    } catch {
      return rejectWithValue('Error al editar certificado');
    }
  },
);

export const deleteCertificate = createAsyncThunk(
  'certificate/deleteCertificate',
  async (id: string, { rejectWithValue }) => {
    try {
      return await deleteCertificateService(id);
    } catch {
      return rejectWithValue('Error al eliminar certificado');
    }
  },
);

export const uploadCertificate = createAsyncThunk(
  'certificate/uploadCertificate',
  async (data: ICertificateUploadForm, { rejectWithValue }) => {
    try {
      return await uploadCertificateService(data);
    } catch {
      return rejectWithValue('Error al cargar certificado');
    }
  },
);

export const downloadCertificate = createAsyncThunk(
  'certificate/downloadCertificate',
  async ({ id, nombreFile }: { id: string; nombreFile: string }, { rejectWithValue }) => {
    try {
      return await downloadCertificateService({ id, nombreFile });
    } catch {
      return rejectWithValue('Error al descargar certificado');
    }
  },
);

export const generarCertificadoTemporal = createAsyncThunk(
  'certificate/generarTemporal',
  async ({ datos, fileName }: { datos: ICertificateGenerate; fileName: string }, { rejectWithValue }) => {
    try {
      return await generarCertificadoTemporalService(datos, fileName);
    } catch {
      return rejectWithValue('Error al generar certificado temporal');
    }
  },
);

export const generarCertificadoTemporalExistente = createAsyncThunk(
  'certificate/generarTemporalExistente',
  async (
    {
      datos,
      fileName,
    }: {
      datos: {
        idCertificado: string;
        frase: string;
        fecha: string | Date;
        idioma: 'es' | 'de';
        temas?: string;
      };
      fileName: string;
    },
    { rejectWithValue },
  ) => {
    try {
      return await generarCertificadoTemporalExistenteService(datos, fileName);
    } catch {
      return rejectWithValue('Error al generar certificado existente');
    }
  },
);

export const descargarCSVEjemplo = createAsyncThunk(
  'certificate/descargarCSV',
  async (tipo: 'PROFESOR' | 'ESTUDIANTE', { rejectWithValue }) => {
    try {
      return await descargarCSVEjemploPeticion(tipo);
    } catch {
      return rejectWithValue('Error al descargar CSV');
    }
  },
);

export const certificateSlice = createSlice({
  name: 'certificate',
  initialState,
  reducers: {
    setCertificates: (state, action: PayloadAction<ICertificate[]>) => {
      state.certificates = action.payload;
    },
    setCountCertificates: (state, action: PayloadAction<number>) => {
      state.countCertificates = action.payload;
    },
    setUserVerification: (state, action: PayloadAction<IUSuariosVerificadosCertificado[]>) => {
      state.userVerification = action.payload;
    },
    setPlantillasCertificados: (state, action: PayloadAction<PlantillaItem[]>) => {
      state.plantillas = action.payload;
    },
    clearUserVerification: (state) => {
      state.userVerification = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(cargarPlantillasCertificados.fulfilled, (state, action) => {
        state.plantillas = action.payload as PlantillaItem[];
      })
      .addCase(cargarCertificados.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cargarCertificados.fulfilled, (state, action) => {
        state.loading = false;
        state.certificates = action.payload.rows;
        state.countCertificates = action.payload.count;
      })
      .addCase(cargarCertificados.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(verificarCertificadoUsuarios.fulfilled, (state, action) => {
        state.userVerification = action.payload as IUSuariosVerificadosCertificado[];
      });
  },
});

export const {
  setCertificates,
  setCountCertificates,
  setUserVerification,
  setPlantillasCertificados,
  clearUserVerification,
} = certificateSlice.actions;
export default certificateSlice.reducer;
